// getVipLevelLivestreamStyleList.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

/**
 * Get all livestream style configs for each VIP level.
 *
 * This selector iterates through the given user level benefit threshold list
 * and retrieves the corresponding remote config values for:
 * - VIP_LEVEL_{level}_LIVESTREAM_STYLE
 * - VIP_LEVEL_{level}_LIVESTREAM_CHAT_LOG_STYLE
 *
 * For example: lv1 ~ lv11 will map to their respective livestream
 * style and chat log style configs.
 *
 * @kind selector
 * @param {Immutable.Map} state - root redux state.
 * @param {number} minRequiredVipBenefitLevel - The minimum required VIP benefit level.
 * @param {number} maxVipBenefitLevel - The maximum VIP benefit level.
 * @return {Array<Array<Object>>}
 * An array grouped by VIP level.
 *
 * Each outer array item represents a single VIP level.
 * The inner array structure:
 * - index 0: livestream user entered notice style config
 * - index 1: livestream chat message log style config
 */
const getVipLevelLivestreamStyleList = createCachedSelector(
  (state, minRequiredVipBenefitLevel, maxVipBenefitLevel) => {
    let vipLevelLivestreamStyleString = '';
    for (let i = minRequiredVipBenefitLevel; i <= maxVipBenefitLevel; i++) {
      const vipLevelLivestreamStyle = getRemoteConfigData(
        state,
        `VIP_LEVEL_${i}_LIVESTREAM_STYLE`,
        undefined,
        { WHITESPACE: '/**/' }
      );
      const vipLevelLivestreamChatLogStyle = getRemoteConfigData(
        state,
        `VIP_LEVEL_${i}_LIVESTREAM_CHAT_LOG_STYLE`,
        undefined,
        { WHITESPACE: '/**/' }
      );
      vipLevelLivestreamStyleString += `${JSON.stringify(vipLevelLivestreamStyle)}~${JSON.stringify(vipLevelLivestreamChatLogStyle)}${maxVipBenefitLevel === i ? '' : '&'}`;
    }
    return vipLevelLivestreamStyleString;
  },
  vipLevelLivestreamStyleString => {
    const result = vipLevelLivestreamStyleString
      .split('&')
      .map(item => item.split('~'));
    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (state, minRequiredVipBenefitLevel, maxVipBenefitLevel) =>
    `${minRequiredVipBenefitLevel}:${maxVipBenefitLevel}`
);

export default getVipLevelLivestreamStyleList;
